import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Upload,
  Button,
  Image,
  Select,
  DatePicker,
  notification,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Joi from "joi";
import axios from "axios";
import moment from "moment";
import config from "../../../../../config/config";
import Imageurldata from "../../../../../Imageurldata";

const { Option } = Select;

const RecipeVideoEdit = ({ isVisible, handleClose, dbData, base_url }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);

  const joiOptions = config.joiOptions;

  /* ===================== FETCH CATEGORIES ===================== */
  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${base_url}/recipe/all`);
      setCategories(res.data.categories || []);
      return res;
    } catch {
      notification.error({ message: "Failed to load categories" });
      return null;
    }
  };

  /* ===================== INIT FORM ===================== */
  useEffect(() => {
    if (isVisible && dbData) {
      fetchCategories().then((res) => {
        const cats = res?.data?.categories || [];

        // Find parent category
        const parentCategory = cats.find((cat) =>
          cat.items?.some((item) => item._id === dbData._id)
        );

        form.setFieldsValue({
          title: dbData.title || "",
          date2: dbData.date2 ? moment(dbData.date2) : null,
          videoUrl: dbData.videoUrl || "",
          categoryId: parentCategory?._id || undefined,
        });

        setImagePreview(
          dbData.desktopImageUrl
            ? Imageurldata + dbData.desktopImageUrl.replaceAll("\\", "/")
            : null
        );
      });
    }
  }, [isVisible, dbData]);

  /* ===================== VALIDATION ===================== */
  const validate = (values) => {
    const schema = Joi.object({
      title: Joi.string().min(1).required().label("Recipe Title"),
      date2: Joi.date().required().label("Video Date"),
      videoUrl: Joi.string().min(5).required().label("Video URL"),
      categoryId: Joi.string().allow(null, ""),
    });

    const { error } = schema.validate(values, joiOptions);
    const errors = {};

    if (error) {
      error.details.forEach((item) => {
        errors[item.path[0]] = item.message;
      });
    }

    return errors;
  };

  /* ===================== SUBMIT ===================== */
  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      date2: values.date2?.toISOString(),
    };

    const errors = validate(payload);
    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("videoId", dbData._id);
      formData.append("title", values.title);
      formData.append("date2", payload.date2);
      formData.append("videoUrl", values.videoUrl);

      if (values.categoryId) {
        formData.append("categoryId", values.categoryId);
      }

      if (imageFile) {
        formData.append("desktopImageUrl", imageFile);
      }

      await axios.put(`${base_url}/recipe/video/update`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      notification.success({
        message: "Recipe video updated successfully",
      });

      form.resetFields();
      setImageFile(null);
      setImagePreview(null);
      setFormError({});
      handleClose();
    } catch (err) {
      notification.error({
        message: "Update failed",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ===================== IMAGE HANDLER ===================== */
  const handleImageChange = (file) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  return (
    <Modal
      title="Edit Recipe Video"
      visible={isVisible}
      onCancel={handleClose}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={loading}
      width={700}
      destroyOnClose
      onOk={() => form.validateFields().then((values) => handleSubmit(values))}
    >
      <Form layout="vertical" form={form}>
        {/* TITLE */}
        <Form.Item
          label="Recipe Title"
          name="title"
          {...(formError.title && {
            help: formError.title,
            validateStatus: "error",
          })}
        >
          <Input placeholder="Enter Recipe title" />
        </Form.Item>

        {/* DATE */}
        <Form.Item
          label="Video Date"
          name="date2"
          {...(formError.date2 && {
            help: formError.date2,
            validateStatus: "error",
          })}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>

        {/* VIDEO URL */}
        <Form.Item
          label="Video URL"
          name="videoUrl"
          {...(formError.videoUrl && {
            help: formError.videoUrl,
            validateStatus: "error",
          })}
        >
          <Input
            placeholder="https://youtu.be/lPrNTCMkw1A"
          />
        </Form.Item>

        {/* CATEGORY */}
        <Form.Item label="Category (Optional)" name="categoryId">
          <Select allowClear placeholder="Select category">
            {categories.map((cat) => (
              <Option key={cat._id} value={cat._id}>
                {cat.categoryTitle}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* IMAGE */}
        <Form.Item
          label="Recipe Video Image"
          extra="Recommended size: 420 x 500 px"
        >
          <Upload
            beforeUpload={handleImageChange}
            maxCount={1}
            accept="image/*"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Select New Image</Button>
          </Upload>

          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <Image
                src={imagePreview}
                width={160}
                height={90}
                style={{ objectFit: "cover", borderRadius: 6 }}
              />
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RecipeVideoEdit;
